// src/features/collaboration/components/CreatePostModal.js
import React, { useState, useEffect } from 'react';
import {
  Box, 
  Modal, 
  Typography, 
  TextField, 
  Button, 
  IconButton,
  Avatar,
  Divider,
  Chip,
  Tab,
  Tabs,
  InputAdornment,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  YouTube as YouTubeIcon,
  PictureAsPdf as PdfIcon,
  LinkOff as LinkOffIcon,
  Delete as DeleteIcon,
  ArticleOutlined as ArticleIcon,
  MenuBook as ResearchIcon,
  Poll as PollIcon,
  Add as AddIcon,
  RemoveCircleOutline as RemoveIcon
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import InlineMathField from '../../../components/math/InlineMathField';
import { THEME_COLORS } from '../../../styles/theme';

const MAX_DOCUMENTS = 5;

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 550 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  overflow: 'auto'
};

// Function to extract video ID from YouTube URLs
const getYoutubeVideoId = (url) => {
  // Handle different YouTube URL formats
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[7].length === 11) ? match[7] : null;
};

// Function to extract TikTok video ID
const getTikTokVideoId = (url) => {
  // Handle TikTok URL formats
  const regExp = /^.*((tiktok\.com\/)([@][A-Za-z0-9_-]+\/video\/)|(v\/)|(\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  
  if (match) {
    // Extract the video ID from the URL
    const videoIdMatch = url.match(/\/video\/(\d+)/);
    return videoIdMatch ? videoIdMatch[1] : null;
  }
  
  return null;
};

const getExistingMediaState = (post) => {
  if (!post) {
    return {
      mediaType: '',
      imagePreview: '',
      videoPreview: '',
      videoUrl: '',
      processedVideoUrl: '',
      videoUrlType: ''
    };
  }

  if ((post.mediaType === 'image' || (!post.mediaType && post.image)) && (post.image || post.mediaUrl)) {
    return {
      mediaType: 'image',
      imagePreview: post.image || post.mediaUrl || '',
      videoPreview: '',
      videoUrl: '',
      processedVideoUrl: '',
      videoUrlType: ''
    };
  }

  if (post.mediaType === 'video') {
    const existingVideoUrl = post.videoUrl || '';
    const existingVideoFile = post.videoFile || '';
    return {
      mediaType: 'video',
      imagePreview: '',
      videoPreview: existingVideoFile,
      videoUrl: existingVideoUrl,
      processedVideoUrl: existingVideoUrl,
      videoUrlType: post.videoUrlType || ''
    };
  }

  return {
    mediaType: '',
    imagePreview: '',
    videoPreview: '',
    videoUrl: '',
    processedVideoUrl: '',
    videoUrlType: ''
  };
};

const getExistingDocuments = (post) => {
  if (!post) {
    return [];
  }

  if (Array.isArray(post.documents) && post.documents.length > 0) {
    return post.documents
      .filter((document) => document?.url)
      .slice(0, MAX_DOCUMENTS)
      .map((document, index) => ({
        id: `existing-document-${index}`,
        name: document.name || `Document ${index + 1}.pdf`,
        url: document.url,
        path: document.path || '',
        isExisting: true
      }));
  }

  const legacyDocumentUrl = post.documentFile || post.documentUrl || '';
  if (!legacyDocumentUrl) {
    return [];
  }

  return [{
    id: 'existing-document-0',
    name: post.documentName || 'Document.pdf',
    url: legacyDocumentUrl,
    path: post.imagePath || '',
    isExisting: true
  }];
};

const CreatePostModal = ({ open, onClose, onSubmit, onUpdate, initialPostType = 'post', initialPost = null }) => {
  const [postContent, setPostContent] = useState('');
  const [postType, setPostType] = useState(initialPostType);
  const [articleTitle, setArticleTitle] = useState('');
  const [articleSubtitle, setArticleSubtitle] = useState('');
  const [articleBody, setArticleBody] = useState('');
  
  // Media states
  const [mediaType, setMediaType] = useState(''); // '', 'image', 'video'
  const [activeTab, setActiveTab] = useState(0);
  
  // Image states
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  
  // Video states
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [processedVideoUrl, setProcessedVideoUrl] = useState('');
  const [videoUrlType, setVideoUrlType] = useState(''); // 'youtube', 'tiktok', 'other'
  
  // Document states
  const [documents, setDocuments] = useState([]);
  
  const [isPublic, setIsPublic] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [pollEnabled, setPollEnabled] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);
  const { userProfile } = useAuth();
  
  const isEditing = Boolean(initialPost?.id);
  const isArticleType = postType === 'article';
  const isResearchType = postType === 'research';
  const isLongFormType = isArticleType || isResearchType;

  // Sync post type when modal opens
  useEffect(() => {
    if (open) {
      setPostType(initialPostType || 'post');
    }
  }, [open, initialPostType]);

  // Populate fields when editing
  useEffect(() => {
    if (open && isEditing) {
      setPostType(initialPost?.postType || 'post');
      setPostContent(initialPost?.content || '');
      setArticleTitle(initialPost?.article?.title || '');
      setArticleSubtitle(initialPost?.article?.subtitle || '');
      setArticleBody(initialPost?.article?.body || '');
      setIsPublic(initialPost?.isPublic ?? true);
      setSelectedSubjects(initialPost?.subjects || []);
      setSelectedGrades(initialPost?.grades || []);
      setSelectedCategory(initialPost?.category || '');
      const existingMedia = getExistingMediaState(initialPost);
      setMediaType(existingMedia.mediaType);
      setActiveTab(0);
      setImageFile(null);
      setImagePreview(existingMedia.imagePreview);
      setVideoFile(null);
      setVideoPreview(existingMedia.videoPreview);
      setVideoUrl(existingMedia.videoUrl);
      setProcessedVideoUrl(existingMedia.processedVideoUrl);
      setVideoUrlType(existingMedia.videoUrlType);
      setDocuments(getExistingDocuments(initialPost));
      // Populate poll fields
      if (initialPost?.poll) {
        setPollEnabled(true);
        setPollQuestion(initialPost.poll.question || '');
        setPollOptions(initialPost.poll.options?.map(o => o.text) || ['', '']);
        setPollAllowMultiple(initialPost.poll.allowMultiple || false);
      } else {
        setPollEnabled(false);
        setPollQuestion('');
        setPollOptions(['', '']);
        setPollAllowMultiple(false);
      }
    } else if (open && !isEditing) {
      setPostContent('');
      setArticleTitle('');
      setArticleSubtitle('');
      setArticleBody('');
      setIsPublic(true);
      setSelectedSubjects([]);
      setSelectedGrades([]);
      setSelectedCategory('');
      setMediaType('');
      setActiveTab(0);
      setImageFile(null);
      setImagePreview('');
      setVideoFile(null);
      setVideoPreview('');
      setVideoUrl('');
      setProcessedVideoUrl('');
      setVideoUrlType('');
      setDocuments([]);
      setPollEnabled(false);
      setPollQuestion('');
      setPollOptions(['', '']);
      setPollAllowMultiple(false);
    }
  }, [open, isEditing, initialPost]);

  // Process video URL
  useEffect(() => {
    if (videoUrl) {
      try {
        const youtubeId = getYoutubeVideoId(videoUrl);
        const tiktokId = getTikTokVideoId(videoUrl);
        
        if (youtubeId) {
          setProcessedVideoUrl(`https://www.youtube.com/embed/${youtubeId}`);
          setVideoUrlType('youtube');
        } else if (tiktokId) {
          setProcessedVideoUrl(`https://www.tiktok.com/embed/${tiktokId}`);
          setVideoUrlType('tiktok');
        } else if (videoUrl.match(/\.(mp4|webm|ogg)(\?.*)?$/i)) {
          setProcessedVideoUrl(videoUrl);
          setVideoUrlType('other');
        } else {
          setProcessedVideoUrl('');
          setVideoUrlType('');
          setError('Invalid video URL. Please enter a YouTube, TikTok URL or direct video file URL.');
        }
      } catch (err) {
        console.error("Error processing video URL:", err);
        setError('Error processing video URL');
      }
    } else {
      setProcessedVideoUrl('');
      setVideoUrlType('');
    }
  }, [videoUrl]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset media states when switching tabs
    if (newValue === 0) { // Upload tab
      setVideoUrl('');
      setProcessedVideoUrl('');
    } else { // URL tab
      setImageFile(null);
      setImagePreview('');
      setVideoFile(null);
      setVideoPreview('');
    }
  };
  
  // Handle image upload
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setMediaType('image');
      
      // Clear other primary media types
      setVideoFile(null);
      setVideoPreview('');
      setVideoUrl('');
      setProcessedVideoUrl('');
      setVideoUrlType('');
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle video upload
  const handleVideoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setVideoFile(file);
      setMediaType('video');
      
      // Clear other primary media types
      setImageFile(null);
      setImagePreview('');
      setVideoUrl('');
      setProcessedVideoUrl('');
      setVideoUrlType('');
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setVideoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle document upload
  const handleDocumentChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) {
      return;
    }

    if (selectedFiles.some((file) => file.type !== 'application/pdf')) {
      setError('Only PDF files are supported');
      e.target.value = '';
      return;
    }

    if (documents.length + selectedFiles.length > MAX_DOCUMENTS) {
      setError(`You can attach up to ${MAX_DOCUMENTS} PDF files per post`);
      e.target.value = '';
      return;
    }

    setDocuments((currentDocuments) => ([
      ...currentDocuments,
      ...selectedFiles.map((file, index) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${index}`,
        name: file.name,
        file,
        isExisting: false
      }))
    ]));
    setError('');
    e.target.value = '';
  };
  
  // Remove primary media
  const removeMedia = () => {
    setImageFile(null);
    setImagePreview('');
    setVideoFile(null);
    setVideoPreview('');
    setVideoUrl('');
    setProcessedVideoUrl('');
    setMediaType('');
    setError('');
  };

  const removeDocument = (documentId) => {
    setDocuments((currentDocuments) => currentDocuments.filter((document) => document.id !== documentId));
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (isLongFormType) {
      if (!articleTitle.trim() || !articleBody.trim()) {
        setError(`Please add a ${isResearchType ? 'research' : 'article'} title and body`);
        return;
      }
    } else if (!postContent.trim() && !mediaType && documents.length === 0 && !pollEnabled) {
      setError('Please add some content, media, or a poll to your post');
      return;
    }

    // Validate poll if enabled
    if (pollEnabled && postType === 'post') {
      const filledOptions = pollOptions.filter(o => o.trim());
      if (!pollQuestion.trim()) {
        setError('Please add a poll question');
        return;
      }
      if (filledOptions.length < 2) {
        setError('Please add at least 2 poll options');
        return;
      }
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const postData = {
        content: postContent,
        isPublic,
        postType,
        subjects: selectedSubjects,
        grades: selectedGrades,
        category: selectedCategory,
        article: isLongFormType ? {
          title: articleTitle.trim(),
          subtitle: articleSubtitle.trim(),
          body: articleBody.trim(),
          wordCount: articleBody.trim().split(/\s+/).filter(Boolean).length
        } : null
      };

      // Build poll object
      if (pollEnabled && postType === 'post') {
        const filledOptions = pollOptions
          .map((text, i) => ({ id: `opt_${i}`, text: text.trim() }))
          .filter(o => o.text);
        const votes = {};
        filledOptions.forEach(o => { votes[o.id] = 0; });
        postData.poll = {
          question: pollQuestion.trim(),
          options: filledOptions,
          allowMultiple: pollAllowMultiple,
          totalVotes: 0,
          votes
        };
      }
      
      // Add media to post data based on type
      if (mediaType === 'image') {
        postData.mediaType = 'image';
        if (imageFile) {
          postData.mediaFile = imageFile;
        } else if (imagePreview) {
          postData.mediaUrl = imagePreview;
        }
      } else if (mediaType === 'video') {
        postData.mediaType = 'video';
        if (videoFile) {
          postData.mediaFile = videoFile;
        } else if (processedVideoUrl) {
          postData.mediaUrl = processedVideoUrl;
          postData.mediaUrlType = videoUrlType;
        }
      }

      postData.documents = documents.map((document) => (
        document.file
          ? { file: document.file, name: document.name }
          : { url: document.url, name: document.name, path: document.path || '' }
      ));
      
      if (isEditing && onUpdate) {
        await onUpdate({ ...postData, id: initialPost.id });
      } else {
        await onSubmit(postData);
      }
      
      // Reset form
      setPostContent('');
      setArticleTitle('');
      setArticleSubtitle('');
      setArticleBody('');
      removeMedia();
      setDocuments([]);
      setIsPublic(true);
      setActiveTab(0);
      setPollEnabled(false);
      setPollQuestion('');
      setPollOptions(['', '']);
      setPollAllowMultiple(false);
      
    } catch (error) {
      console.error('Error creating post:', error);
      setError(error?.message || 'Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="create-post-modal"
    >
      <Box sx={style}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography id="create-post-modal" variant="h6" fontWeight="bold">
            {isEditing
              ? (isArticleType ? 'Edit Article' : isResearchType ? 'Edit Deep Research' : 'Edit Post')
              : (isArticleType ? 'Create Article' : isResearchType ? 'Create Deep Research' : 'Create Post')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar 
            src={userProfile?.profileImage} 
            alt={userProfile?.name}
            sx={{ mr: 1.5, bgcolor: THEME_COLORS.primary }}
          >
            {userProfile?.name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {userProfile?.name}
            </Typography>
            <Chip
              icon={isPublic ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
              label={isPublic ? "Public" : "Connections Only"}
              size="small"
              variant="outlined"
              onClick={() => setIsPublic(!isPublic)}
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        </Box>
        
        <Box display="flex" alignItems="center" mb={2} gap={1}>
          <ToggleButtonGroup
            value={postType}
            exclusive
            onChange={(event, newValue) => {
              if (newValue) {
                setPostType(newValue);
                setError('');
                if (newValue === 'article' || newValue === 'research') {
                  setActiveTab(0);
                  if (mediaType === 'video') {
                    removeMedia();
                  }
                }
              }
            }}
            size="small"
            disabled={isEditing}
          >
            <ToggleButton value="post">Post</ToggleButton>
            <ToggleButton value="article">
              <Box display="flex" alignItems="center" gap={0.5}>
                <ArticleIcon sx={{ fontSize: 18 }} />
                Article
              </Box>
            </ToggleButton>
            <ToggleButton value="research">
              <Box display="flex" alignItems="center" gap={0.5}>
                <ResearchIcon sx={{ fontSize: 18 }} />
                Deep Research
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
          {isLongFormType && (
            <Chip label={isResearchType ? "Deep Research" : "Article"} size="small" color="primary" />
          )}
        </Box>

        {isLongFormType ? (
          <>
            <TextField
              fullWidth
              label={isResearchType ? "Research title" : "Article title"}
              placeholder={isResearchType ? "Enter a clear research title" : "Enter a clear, specific title"}
              variant="outlined"
              value={articleTitle}
              onChange={(e) => setArticleTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Subtitle (optional)"
              placeholder="Add a short subtitle"
              variant="outlined"
              value={articleSubtitle}
              onChange={(e) => setArticleSubtitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                {isResearchType ? 'Research body' : 'Article body'}
              </Typography>
              <InlineMathField
                value={articleBody}
                onChange={setArticleBody}
                placeholder={isResearchType ? "Write your research here..." : "Write your article here..."}
                rows={8}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                Post note (optional)
              </Typography>
              <InlineMathField
                value={postContent}
                onChange={setPostContent}
                placeholder="Add a short note for the feed"
                rows={2}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ mb: 2 }}>
            <InlineMathField
              value={postContent}
              onChange={setPostContent}
              placeholder="What would you like to share with educators?"
              rows={4}
            />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
            Category
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="post-category-label">Select category</InputLabel>
            <Select
              labelId="post-category-label"
              label="Select category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Leadership & Management">Leadership & Management</MenuItem>
              <MenuItem value="Teaching & Learning">Teaching & Learning</MenuItem>
              <MenuItem value="Professional Development">Professional Development</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
            Subjects (optional)
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="post-subjects-label">Select subjects</InputLabel>
            <Select
              labelId="post-subjects-label"
              label="Select subjects"
              multiple
              value={selectedSubjects}
              onChange={(e) => setSelectedSubjects(e.target.value)}
              renderValue={(selected) => (selected || []).join(', ')}
            >
              {(userProfile?.subjects || []).map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
            Grades (optional)
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="post-grades-label">Select grades</InputLabel>
            <Select
              labelId="post-grades-label"
              label="Select grades"
              multiple
              value={selectedGrades}
              onChange={(e) => setSelectedGrades(e.target.value)}
              renderValue={(selected) => (selected || []).join(', ')}
            >
              {['Grade 7','Grade 8','Grade 9','Grade 10','Grade 11','Grade 12'].map((grade) => (
                <MenuItem key={grade} value={grade}>
                  {grade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {/* Poll Section - only for posts, not articles */}
        {postType === 'post' && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={pollEnabled}
                  onChange={(e) => setPollEnabled(e.target.checked)}
                  disabled={isEditing && (initialPost?.poll?.totalVotes > 0)}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <PollIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2">Add a poll</Typography>
                </Box>
              }
            />
            {isEditing && initialPost?.poll?.totalVotes > 0 && pollEnabled && (
              <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                Poll editing is disabled because votes have been cast.
              </Alert>
            )}
            {pollEnabled && (
              <Box sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Poll question"
                  placeholder="Ask a question..."
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  disabled={isEditing && initialPost?.poll?.totalVotes > 0}
                  sx={{ mb: 2 }}
                />
                {pollOptions.map((option, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Option ${index + 1}`}
                      placeholder={`Option ${index + 1}`}
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...pollOptions];
                        newOptions[index] = e.target.value;
                        setPollOptions(newOptions);
                      }}
                      disabled={isEditing && initialPost?.poll?.totalVotes > 0}
                    />
                    {pollOptions.length > 2 && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newOptions = pollOptions.filter((_, i) => i !== index);
                          setPollOptions(newOptions);
                        }}
                        disabled={isEditing && initialPost?.poll?.totalVotes > 0}
                        sx={{ ml: 0.5 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {pollOptions.length < 4 && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setPollOptions([...pollOptions, ''])}
                    disabled={isEditing && initialPost?.poll?.totalVotes > 0}
                    sx={{ mt: 0.5 }}
                  >
                    Add option
                  </Button>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pollAllowMultiple}
                        onChange={(e) => setPollAllowMultiple(e.target.checked)}
                        size="small"
                        disabled={isEditing && initialPost?.poll?.totalVotes > 0}
                      />
                    }
                    label={<Typography variant="body2">Allow multiple selections</Typography>}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {/* Media Preview Section */}
        {imagePreview && (
          <Box position="relative" mb={2}>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)',
                }
              }}
              onClick={removeMedia}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <img
              src={imagePreview}
              alt="Post preview"
              style={{ 
                width: '100%', 
                borderRadius: 8,
                maxHeight: '200px',
                objectFit: 'cover'
              }}
            />
          </Box>
        )}
        
        {videoPreview && (
          <Box position="relative" mb={2}>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)',
                }
              }}
              onClick={removeMedia}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <video
              src={videoPreview}
              controls
              style={{ 
                width: '100%', 
                borderRadius: 8,
                maxHeight: '200px',
              }}
            />
          </Box>
        )}
        
        {processedVideoUrl && (
          <Box position="relative" mb={2}>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                zIndex: 1,
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)',
                }
              }}
              onClick={removeMedia}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <iframe
                width="100%"
                height="200"
                src={processedVideoUrl}
                title="Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          </Box>
        )}
        
        {documents.length > 0 && (
          <Box mb={2}>
            {documents.map((document) => (
              <Box 
                key={document.id}
                position="relative" 
                mb={1} 
                p={2} 
                sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <PdfIcon sx={{ color: '#f44336', mr: 1, fontSize: 32 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{document.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    PDF Document
                  </Typography>
                </Box>
                {document.url && (
                  <Button
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ mr: 0.5 }}
                  >
                    Open
                  </Button>
                )}
                <IconButton size="small" onClick={() => removeDocument(document.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Typography variant="caption" color="textSecondary">
              {documents.length}/{MAX_DOCUMENTS} PDF files attached
            </Typography>
          </Box>
        )}
        
        <>
          <Divider sx={{ mb: 2 }} />
          
          {/* Media Selection Section */}
          <Box>
              <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ mb: 2 }}>
                <Tab label="Upload File" />
                {!isLongFormType && <Tab label="Add URL" />}
              </Tabs>
              
              {activeTab === 0 ? (
                <Box display="flex" justifyContent="space-around" alignItems="center">
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="upload-image-button"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="upload-image-button">
                    <IconButton 
                      component="span" 
                      color={mediaType === 'image' ? 'primary' : 'default'}
                      sx={{ 
                        p: 2,
                        border: mediaType === 'image' ? `1px solid ${THEME_COLORS.primary}` : '1px solid transparent',
                        bgcolor: mediaType === 'image' ? 'rgba(39, 35, 97, 0.08)' : 'transparent'
                      }}
                    >
                      <ImageIcon fontSize="large" />
                    </IconButton>
                  </label>
                  
                  {!isLongFormType && (
                    <>
                      <input
                        accept="video/*"
                        style={{ display: 'none' }}
                        id="upload-video-button"
                        type="file"
                        onChange={handleVideoChange}
                      />
                      <label htmlFor="upload-video-button">
                        <IconButton 
                          component="span" 
                          color={mediaType === 'video' && videoFile ? 'primary' : 'default'}
                          sx={{ 
                            p: 2,
                            border: mediaType === 'video' && videoFile ? `1px solid ${THEME_COLORS.primary}` : '1px solid transparent',
                            bgcolor: mediaType === 'video' && videoFile ? 'rgba(39, 35, 97, 0.08)' : 'transparent'
                          }}
                        >
                          <VideoIcon fontSize="large" />
                        </IconButton>
                      </label>
                      
                      <input
                        accept="application/pdf"
                        style={{ display: 'none' }}
                        id="upload-document-button"
                        type="file"
                        multiple
                        onChange={handleDocumentChange}
                      />
                      <label htmlFor="upload-document-button">
                        <IconButton 
                          component="span" 
                          color={documents.length > 0 ? 'primary' : 'default'}
                          sx={{ 
                            p: 2,
                            border: documents.length > 0 ? `1px solid ${THEME_COLORS.primary}` : '1px solid transparent',
                            bgcolor: documents.length > 0 ? 'rgba(39, 35, 97, 0.08)' : 'transparent'
                          }}
                          disabled={documents.length >= MAX_DOCUMENTS}
                        >
                          <PdfIcon fontSize="large" />
                        </IconButton>
                      </label>
                    </>
                  )}
                </Box>
              ) : !isLongFormType ? (
                <Box>
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Video URL (YouTube or TikTok)"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={videoUrl}
                      onChange={(e) => {
                        setVideoUrl(e.target.value);
                        if (e.target.value) {
                          setMediaType('video');
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <YouTubeIcon />
                          </InputAdornment>
                        ),
                        endAdornment: videoUrl && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => {
                              setVideoUrl('');
                              setProcessedVideoUrl('');
                              setVideoUrlType('');
                              setMediaType('');
                            }}>
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                </Box>
              ) : null}
            </Box>
        </>
        
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || (isLongFormType ? (!articleTitle.trim() || !articleBody.trim()) : (!postContent.trim() && !mediaType && documents.length === 0 && !pollEnabled)) || (pollEnabled && postType === 'post' && (!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2))}
            sx={{ 
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                {isEditing ? 'Updating...' : 'Posting...'}
              </>
            ) : (
              isEditing ? 'Update' : (isArticleType ? 'Publish Article' : isResearchType ? 'Publish Research' : 'Post')
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreatePostModal;
